import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { FormProvider, useForm, useFormState } from 'react-hook-form';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import useFeatureFlags from 'api/useFeatureFlags';
import useImages from 'api/useImages';
import useOffers from 'api/useOffers';
import { useProduct } from 'api/useProduct';
import {
  EFeatureFlag,
  EPopupSubType,
  ERewardCalendarFormState,
  OfferType
} from 'constants/enums';
import { OfferFormLayout } from 'views/Offers/components/OfferFormLayout';
import { useOfferDesignOptions } from 'views/Offers/hooks/useOfferDesignOptions';
import { useOfferFormSubmit } from 'views/Offers/hooks/useOfferFormSubmit';
import { DEFAULT_OFFER_DESIGN_EXTERNAL_ID } from 'views/Offers/Offer.consts';
import { checkUniqueness } from 'views/Offers/Offer.utils';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

import DialogModal from 'components/DialogModal/DialogModal';
import FormSectionsTabs from 'components/FormSectionsTabs';
import { Form } from 'components/ui';
import { Tabs, TabsContent } from 'components/ui/tabs';

import ConfigurationTab from './components/Tabs/ConfigurationTab';
import OfferBehaviorTab from './components/Tabs/OfferBehaviorTab';
import SettingsTab from './components/Tabs/SettingsTab';
import {
  REWARD_CALENDAR_DEFAULT_VALUES,
  REWARD_CALENDAR_OFFERS_LIST_STALE_MS,
  REWARD_CALENDAR_SCHEMA,
  REWARD_CALENDAR_TABS,
  RewardCalendarFormFields
} from './RewardCalendarForm.consts';
import { IRewardCalendarFormProps } from './RewardCalendarForm.types';
import {
  buildRewardCalendarInitialValues,
  buildRewardCalendarPayload,
  mapOfferToRewardCalendarWeeks
} from './RewardCalendarForm.utils';
import { RewardCalendarPreview } from './RewardCalendarPreview';

const RewardCalendarForm: FC<IRewardCalendarFormProps> = ({
  edit = false,
  dup = false
}) => {
  const { publisherId } = useParams();
  const { featureFlags, isLoading } = useFeatureFlags({ publisherId });

  if (isLoading) {
    return null;
  }

  if (!featureFlags?.[EFeatureFlag.DASHBOARD_REWARD_CALENDAR]) {
    return <Navigate to="../popups" replace />;
  }

  return <RewardCalendarFormContent edit={edit} dup={dup} />;
};

const RewardCalendarFormContent: FC<IRewardCalendarFormProps> = ({
  edit = false,
  dup = false
}) => {
  const { publisherId, popupId } = useParams();
  const navigate = useNavigate();
  const { getProducts } = useProduct(publisherId);
  const {
    addPopUpOffer,
    updatePopUpOffer,
    getOffer,
    getOffers,
    formatProductQuantity
  } = useOffers({
    offerId: popupId,
    offerType: OfferType.POPUP,
    offerSubType: EPopupSubType.REWARD_CALENDAR,
    ...(edit || dup
      ? { listStaleTime: REWARD_CALENDAR_OFFERS_LIST_STALE_MS }
      : {})
  });

  const { submitOffer } = useOfferFormSubmit({
    edit,
    dup,
    offerLabel: 'Reward Calendar',
    addOffer: addPopUpOffer,
    updateOffer: updatePopUpOffer,
    onSuccess: (): void => {
      getOffers.refetch();
    }
  });

  const { getImages } = useImages(publisherId);
  const isImagesReady = !getImages.isLoading && !!getImages.data;
  const { offerDesignOptions, defaultOfferDesignId: defaultOfferUiId } =
    useOfferDesignOptions({
      publisherId: publisherId || '',
      offerDesignType: OfferDesignTypes.PopUp,
      defaultExternalId: DEFAULT_OFFER_DESIGN_EXTERNAL_ID[OfferType.POPUP]
    });

  const [activeTab, setActiveTab] = useState<ERewardCalendarFormState>(
    ERewardCalendarFormState.GENERAL
  );
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] =
    useState(false);

  const offers = getOffers.data?.offers || [];
  const currentOffer = getOffer.data?.result;

  const form = useForm<RewardCalendarFormFields>({
    resolver: zodResolver(REWARD_CALENDAR_SCHEMA),
    mode: 'onChange',
    defaultValues: REWARD_CALENDAR_DEFAULT_VALUES
  });

  const { isDirty, isValid } = useFormState({
    control: form.control
  });

  const isCreateFlow = !edit && !dup && !popupId;
  const createDirtyBaselineDoneRef = useRef(false);

  const watchedName = form.watch('name');
  const watchedPublisherOfferId = form.watch('publisherOfferId');

  const isNameTaken = !checkUniqueness(
    watchedName,
    'name',
    offers,
    currentOffer,
    edit
  );
  const isPublisherOfferIdTaken = !checkUniqueness(
    watchedPublisherOfferId,
    'publisherOfferId',
    offers,
    currentOffer,
    edit
  );

  useEffect(() => {
    if (!popupId && defaultOfferUiId) {
      form.setValue('offerUiId', defaultOfferUiId, {
        shouldDirty: false,
        shouldValidate: true
      });
    }

    if (!((edit || dup) && currentOffer)) return;

    const weeks = mapOfferToRewardCalendarWeeks(currentOffer);

    form.reset(
      buildRewardCalendarInitialValues({
        currentOffer,
        defaultOfferUiId,
        edit,
        dup,
        weeks
      })
    );
  }, [edit, dup, currentOffer, form, popupId, defaultOfferUiId]);

  useEffect(() => {
    if (!isCreateFlow || !isImagesReady || createDirtyBaselineDoneRef.current) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      if (createDirtyBaselineDoneRef.current) return;
      form.reset(form.getValues());
      createDirtyBaselineDoneRef.current = true;
    }, 0);

    return (): void => {
      window.clearTimeout(timeoutId);
    };
  }, [isCreateFlow, form, isImagesReady]);

  const onSubmit = useCallback(
    (data: RewardCalendarFormFields): void => {
      submitOffer(buildRewardCalendarPayload({ data }), popupId);
    },
    [submitOffer, popupId]
  );

  const formTitle = useMemo((): string => {
    if (edit) {
      return `Edit Reward Calendar (${watchedName || currentOffer?.name || ''})`;
    }
    if (dup) {
      return 'Duplicate Reward Calendar';
    }
    return 'Create Reward Calendar';
  }, [edit, dup, watchedName, currentOffer?.name]);

  const isFormValid =
    isValid && (dup || isDirty) && !isNameTaken && !isPublisherOfferIdTaken;

  const handleSave = useCallback((): void => {
    form.handleSubmit(onSubmit)();
  }, [form, onSubmit]);

  const navigateUp = useCallback((): void => {
    navigate('../');
  }, [navigate]);

  const leaveForm = useCallback((): void => {
    setIsUnsavedChangesModalOpen(false);
    navigateUp();
  }, [navigateUp]);

  const handleCloseAttempt = useCallback((): void => {
    if (isDirty) {
      setIsUnsavedChangesModalOpen(true);
      return;
    }
    navigateUp();
  }, [isDirty, navigateUp]);

  const buttonText = edit ? 'Update' : 'Save';

  return (
    <>
      <OfferFormLayout
        title={formTitle}
        mainButtonText={buttonText}
        isFormValid={isFormValid}
        onSave={handleSave}
        onClose={handleCloseAttempt}
        previewContent={<RewardCalendarPreview />}
      >
        {isImagesReady && (
          <FormProvider {...form}>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="pt-10 pb-10 w-[528px] mx-auto flex flex-col gap-y-9"
              >
                <Tabs
                  value={activeTab}
                  onValueChange={(value) =>
                    setActiveTab(value as ERewardCalendarFormState)
                  }
                  className="w-full"
                >
                  <FormSectionsTabs
                    currentTab={activeTab}
                    tabs={REWARD_CALENDAR_TABS}
                  />
                  <TabsContent
                    value={ERewardCalendarFormState.GENERAL}
                    forceMount
                    className="mt-9 data-[state=inactive]:hidden"
                  >
                    <SettingsTab
                      offerDesignOptions={offerDesignOptions}
                      isNameTaken={isNameTaken}
                      isPublisherOfferIdTaken={isPublisherOfferIdTaken}
                    />
                  </TabsContent>
                  <TabsContent
                    value={ERewardCalendarFormState.CONFIGURATION}
                    forceMount
                    className="mt-9 data-[state=inactive]:hidden"
                  >
                    <ConfigurationTab
                      getProducts={getProducts}
                      formatProductQuantity={formatProductQuantity}
                    />
                  </TabsContent>
                  <TabsContent
                    value={ERewardCalendarFormState.OFFER_BEHAVIOR}
                    forceMount
                    className="mt-9 data-[state=inactive]:hidden"
                  >
                    <OfferBehaviorTab />
                  </TabsContent>
                </Tabs>
              </form>
            </Form>
          </FormProvider>
        )}
      </OfferFormLayout>
      {isUnsavedChangesModalOpen && (
        <DialogModal
          isOpen={isUnsavedChangesModalOpen}
          title="Unsaved Changes"
          description={
            "You've made changes that haven't been saved. Leaving now will discard your changes."
          }
          dataTestId="RewardCalendar-UnsavedChangesModal"
          modalActions={{
            onConfirm: {
              label: 'Continue editing',
              action: (): void => {
                setIsUnsavedChangesModalOpen(false);
              },
              testId: 'RewardCalendar-UnsavedChangesModal-continue'
            },
            onClose: {
              label: 'Leave',
              action: leaveForm,
              variant: 'outline',
              testId: 'RewardCalendar-UnsavedChangesModal-leave'
            }
          }}
        />
      )}
    </>
  );
};

export { RewardCalendarForm };
